import { useCallback, useEffect, useRef, useState } from 'react';

// Two other independent implementations of this pixel-based drag-resize
// pattern exist: ProposalCenter.tsx's list-panel resize, and ReasoningChat's
// resize handling (percentage-based, not pixel-based — out of scope for this
// hook's pixel-only design). Migrating those to this shared hook is a
// reasonable follow-up.

interface UseResizablePanelOptions {
  initialWidth: number;
  minWidth: number;
  maxWidthPercent: number;
  /**
   * Which screen edge the panel hugs — determines how a horizontal drag maps
   * to a width change. A 'right' panel (default) grows as the handle moves
   * left; a 'left' panel grows as the handle moves right.
   */
  edge?: 'left' | 'right';
}

interface UseResizablePanelResult {
  width: number;
  isResizing: boolean;
  handleResizeStart: (e: React.MouseEvent) => void;
  /**
   * Adjust the width by a signed delta (positive grows), clamped to the
   * min/max bounds. Shaped for EdgeResizeHandle's onKeyboardResize, which
   * owns the arrow-key → delta mapping.
   */
  resizeBy: (delta: number) => void;
}

export function useResizablePanel({
  initialWidth,
  minWidth,
  maxWidthPercent,
  edge = 'right',
}: UseResizablePanelOptions): UseResizablePanelResult {
  const [width, setWidth] = useState(initialWidth);
  const [isResizing, setIsResizing] = useState(false);
  const dragStartXRef = useRef(0);
  const dragStartWidthRef = useRef(initialWidth);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragStartXRef.current = e.clientX;
      dragStartWidthRef.current = width;
      setIsResizing(true);
      document.body.classList.add('col-resize-active');
    },
    [width]
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) {
        return;
      }
      // On viewports narrow enough that the percentage cap drops below the
      // minimum, the minimum wins — clamping to a max below min would
      // return widths under the configured floor.
      const maxWidth = Math.max(
        Math.floor(window.innerWidth * maxWidthPercent),
        minWidth
      );
      const deltaX =
        edge === 'right'
          ? dragStartXRef.current - e.clientX
          : e.clientX - dragStartXRef.current;
      const newWidth = Math.min(
        Math.max(dragStartWidthRef.current + deltaX, minWidth),
        maxWidth
      );
      requestAnimationFrame(() => setWidth(newWidth));
    };

    const handleMouseUp = () => {
      if (isResizing) {
        setIsResizing(false);
        document.body.classList.remove('col-resize-active');
      }
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.classList.remove('col-resize-active');
    };
  }, [isResizing, minWidth, maxWidthPercent, edge]);

  const resizeBy = useCallback(
    (delta: number) => {
      // Same min-wins normalization as the drag path (see handleMouseMove).
      const maxWidth = Math.max(
        Math.floor(window.innerWidth * maxWidthPercent),
        minWidth
      );
      setWidth(prev => Math.min(Math.max(prev + delta, minWidth), maxWidth));
    },
    [minWidth, maxWidthPercent]
  );

  return { width, isResizing, handleResizeStart, resizeBy };
}
